import { useCallback, useEffect, useMemo, useState } from 'react';

import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import UIResource from '@/components/UIResource';
import { FILTER_KEY } from '@/ducks/filters/types';
import { getSearchParamsValue } from '@/ducks/filters/utils/primitive';
import { getQFAdditionalParams } from '@/features/quickFindFilters/helpers';
import { useQFActiveLink } from '@/features/quickFindFilters/hooks';
import uniq from '@/helpers/uniq';
import { ensureArray } from '@/helpers/util';
import { setTrackingDestinationFilter, trackResetFilters } from '@/helpers/util/tracking';
import { useAppDispatch, useAppSelector } from '@/store';

import { selectDestinationsFilterData, selectDestinationType } from './selectors';
import { type AllItemsIds, DestinationType } from './types';
import {
  addItemsIds,
  type DestinationRefinementProps,
  getAppliedItemsIds,
  getCheckedItemsIds,
  getEmptyItemsIds,
  getItemLabel,
  isItemIdSelected,
  isWholeChecked,
  removeItemsIds,
} from './utils';

const useDestinationRefinementData = (props: DestinationRefinementProps) => {
  const dispatch = useAppDispatch();

  const {
    onComplete,
    popoverProps,
    popoverProps: { closePopover, isOpen },
  } = props;

  const [ariaLiveMessageItems, setItemsAriaLiveMessage] = useState('');
  const [ariaLiveMessageRegions, setRegionsAriaLiveMessage] = useState('');

  const data = useAppSelector(selectDestinationsFilterData);
  const appliedDestinationType = useAppSelector(selectDestinationType);

  const getAppliedData = useCallback(() => {
    return {
      appliedDestinationType,
      appliedItemsIds: getAppliedItemsIds(data),
      appliedRegionId:
        data[appliedDestinationType].find((region) => region.selected)?.id || data[appliedDestinationType][0]?.id,
    };
  }, [data, appliedDestinationType]);

  const { appliedItemsIds, appliedRegionId } = useMemo(() => getAppliedData(), [getAppliedData]);

  const [destinationType, setDestinationType] = useState(appliedDestinationType);
  const [selectedRegionId, setSelectedRegionId] = useState(appliedRegionId);
  const [allItemsIds, setAllItemsIds] = useState<AllItemsIds>(appliedItemsIds);

  const getRegions = useCallback((type?: DestinationType) => data[type || destinationType], [data, destinationType]);
  const getRegion = useCallback(
    (id?: string, type?: DestinationType) => {
      const regions = getRegions(type);
      return id ? regions.find((region) => region.id === id)! : regions[0]!;
    },
    [getRegions],
  );

  const selectedRegion = useMemo(() => getRegion(selectedRegionId), [getRegion, selectedRegionId]);

  const activeQFLink = useQFActiveLink();
  const warningText = UIResource({ id: 'QuickFind.warning.regions', values: { label: activeQFLink?.linkTitle } });
  const warning = useMemo(() => {
    if (!activeQFLink) {
      return null;
    }
    const QFAdditionalParams = getQFAdditionalParams(activeQFLink);
    const QFRegion = {
      [DestinationType.ITINERARIES]: ensureArray(
        getSearchParamsValue(FILTER_KEY.destPackagesRegions, QFAdditionalParams),
      ),
      [DestinationType.PORTS_OF_CALL]: ensureArray(
        getSearchParamsValue(FILTER_KEY.destPortsRegions, QFAdditionalParams),
      ),
    };
    const QFIds = {
      [DestinationType.ITINERARIES]: ensureArray(getSearchParamsValue(FILTER_KEY.destPackages, QFAdditionalParams)),
      [DestinationType.PORTS_OF_CALL]: ensureArray(getSearchParamsValue(FILTER_KEY.destPorts, QFAdditionalParams)),
    };

    if (!QFRegion[destinationType]?.length) {
      return null;
    }

    if (
      Object.entries(allItemsIds[destinationType] ?? {}).some(
        ([id, value]) =>
          QFRegion[destinationType]?.includes(id) &&
          (!QFIds[destinationType]?.length || value.some((itemId) => QFIds[destinationType]?.includes(itemId))),
      )
    ) {
      return null;
    }

    return warningText;
  }, [activeQFLink, allItemsIds, destinationType, warningText]);

  const resetToAppliedData = useCallback(() => {
    const { appliedDestinationType, appliedItemsIds, appliedRegionId } = getAppliedData();
    setDestinationType(appliedDestinationType);
    setSelectedRegionId(appliedRegionId);
    setAllItemsIds(appliedItemsIds);
  }, [getAppliedData]);

  useEffect(() => {
    resetToAppliedData();
  }, [resetToAppliedData, isOpen]);

  const processRegionsAriaLiveMessage = useCallback(
    (newItemsIds: AllItemsIds, regionId?: string) => {
      if (regionId) {
        const region = getRegion(regionId);
        const announceLabel = `${region.label}, checkbox`;
        const isAllChecked = isWholeChecked(region, destinationType, newItemsIds);
        const checkedIds = getCheckedItemsIds(newItemsIds, destinationType, region.id);
        if (isAllChecked) {
          setRegionsAriaLiveMessage(`All checked, ${announceLabel}`);
        } else {
          setRegionsAriaLiveMessage(
            checkedIds.length > 0 ? `${checkedIds.length} checked, ${announceLabel}` : `Not checked, ${announceLabel}`,
          );
        }
      }
    },
    [getRegion, destinationType],
  );

  const processItemsAriaLiveMessage = useCallback(
    (itemId: string, newItemsIds: AllItemsIds) => {
      const label = getItemLabel(itemId, selectedRegion.items, destinationType);
      const announceLabel = `${label}, checkbox`;
      const checked = isItemIdSelected(itemId, newItemsIds, destinationType, selectedRegion.id);
      setItemsAriaLiveMessage(checked ? `Checked, ${announceLabel}` : `Not checked, ${announceLabel}`);
    },
    [destinationType, selectedRegion],
  );

  const onRegionCheck = useCallback(
    (regionId?: string) => {
      const region = getRegion(regionId);
      let newIds;
      if (regionId && !isEmpty(allItemsIds[destinationType]?.[regionId])) {
        newIds = removeItemsIds(allItemsIds, destinationType, allItemsIds[destinationType]![regionId]!);
        setAllItemsIds(newIds);
      } else {
        newIds = addItemsIds(data, allItemsIds, destinationType, region.allItemsIds);
        setAllItemsIds(newIds);
      }
      processRegionsAriaLiveMessage(newIds, regionId);
    },
    [getRegion, allItemsIds, destinationType, processRegionsAriaLiveMessage, data],
  );

  const onRegionSelect = useCallback((regionId: string, e: React.UIEvent) => {
    e.preventDefault();
    setSelectedRegionId(regionId);
  }, []);

  const onDestinationTypeChange = useCallback((value: string) => {
    setDestinationType(value as DestinationType);
  }, []);

  const onItemCheck = useCallback(
    (itemId: string) => {
      let newIds;
      if (isItemIdSelected(itemId, allItemsIds, destinationType, selectedRegionId)) {
        newIds = removeItemsIds(allItemsIds, destinationType, [itemId]);
        setAllItemsIds(newIds);
      } else {
        newIds = addItemsIds(data, allItemsIds, destinationType, [itemId]);
        setAllItemsIds(newIds);
      }
      processItemsAriaLiveMessage(itemId, newIds);
    },
    [allItemsIds, destinationType, selectedRegionId, data, processItemsAriaLiveMessage],
  );

  const isWholeCheckedById = useCallback(
    (regionId: string, destinationType: DestinationType) => {
      const region = getRegion(regionId, destinationType);
      return isWholeChecked(region, destinationType, allItemsIds);
    },
    [getRegion, allItemsIds],
  );

  const onSubmit = useCallback(
    (event: React.UIEvent) => {
      const regionsIds = Object.keys(allItemsIds[destinationType] || {}).filter(
        (id) => !isEmpty(allItemsIds[destinationType]?.[id] || []),
      );

      const fullySelectedRegions = regionsIds.filter((id) => isWholeCheckedById(id, destinationType));
      const partiallySelectedRegions = regionsIds.filter((id) => {
        const itemsIds = allItemsIds[destinationType]?.[id] || [];

        return (
          !fullySelectedRegions.length ||
          (!fullySelectedRegions.includes(id) &&
            fullySelectedRegions.some((regionId) => {
              const regionItemsIds = allItemsIds[destinationType]?.[regionId] || [];
              return !itemsIds.every((itemId) => regionItemsIds.includes(itemId));
            }))
        );
      });

      const itemsIds = uniq(
        partiallySelectedRegions.reduce<string[]>((ids: string[], id: string) => {
          const itemsIds = allItemsIds[destinationType]?.[id] || [];
          const uniqueItemsIds = itemsIds.filter((itemId) => {
            return !fullySelectedRegions.some((regionId) => {
              const regionItemsIds = allItemsIds[destinationType]?.[regionId] || [];
              return regionItemsIds.includes(itemId);
            });
          });

          return [...ids, ...uniqueItemsIds];
        }, []),
      );

      const isResetVoyages = !allItemsIds[destinationType] || isEmpty(allItemsIds[destinationType]);
      const isResetQF = !!warning;
      onComplete?.(
        { destinationType, itemsIds, regionsIds: fullySelectedRegions },
        isResetVoyages,
        isResetQF,
        activeQFLink,
      );

      closePopover?.({ event });
      dispatch(setTrackingDestinationFilter(isWholeCheckedById));
    },
    [isWholeCheckedById, destinationType, closePopover, dispatch, allItemsIds, warning, onComplete, activeQFLink],
  );

  const onReset = useCallback(
    (e?: { resetAll?: boolean }) => {
      if (!isEmpty(e) && !e.resetAll) {
        trackResetFilters('destination');
      }

      if (activeQFLink) {
        const QFAdditionalParams = getQFAdditionalParams(activeQFLink);
        const QFRegion = {
          [DestinationType.ITINERARIES]: ensureArray(
            getSearchParamsValue(FILTER_KEY.destPackagesRegions, QFAdditionalParams),
          ),
          [DestinationType.PORTS_OF_CALL]: ensureArray(
            getSearchParamsValue(FILTER_KEY.destPortsRegions, QFAdditionalParams),
          ),
        };
        const QFIds = {
          [DestinationType.ITINERARIES]: ensureArray(getSearchParamsValue(FILTER_KEY.destPackages, QFAdditionalParams)),
          [DestinationType.PORTS_OF_CALL]: ensureArray(getSearchParamsValue(FILTER_KEY.destPorts, QFAdditionalParams)),
        };

        if (QFRegion[destinationType]?.length) {
          const newIds = QFRegion[destinationType].reduce<AllItemsIds>((acc, regionId) => {
            const region = getRegion(regionId.trim());
            const ids = region.allItemsIds.filter(
              (itemId) => !QFIds[destinationType]?.length || QFIds[destinationType]?.includes(itemId),
            );
            return region ? addItemsIds(data, acc, destinationType, ids) : acc;
          }, getEmptyItemsIds());
          setAllItemsIds(newIds);

          return;
        }
      }
      setAllItemsIds(getEmptyItemsIds());
    },
    [activeQFLink, onRegionCheck, destinationType],
  );

  const isDataApplied = useMemo(() => {
    let defaultItemIds = getEmptyItemsIds();

    if (activeQFLink) {
      const QFAdditionalParams = getQFAdditionalParams(activeQFLink);
      const QFRegion = {
        [DestinationType.ITINERARIES]: ensureArray(
          getSearchParamsValue(FILTER_KEY.destPackagesRegions, QFAdditionalParams),
        ),
        [DestinationType.PORTS_OF_CALL]: ensureArray(
          getSearchParamsValue(FILTER_KEY.destPortsRegions, QFAdditionalParams),
        ),
      };
      const QFIds = {
        [DestinationType.ITINERARIES]: ensureArray(getSearchParamsValue(FILTER_KEY.destPackages, QFAdditionalParams)),
        [DestinationType.PORTS_OF_CALL]: ensureArray(getSearchParamsValue(FILTER_KEY.destPorts, QFAdditionalParams)),
      };

      if (QFRegion[DestinationType.ITINERARIES]?.length || QFRegion[DestinationType.PORTS_OF_CALL]?.length) {
        Object.keys(QFRegion).forEach((type) => {
          if (QFRegion[type]?.length) {
            QFRegion[type].forEach((regionId) => {
              const region = getRegion(regionId?.trim());
              const ids = region.allItemsIds.filter((itemId) => !QFIds[type]?.length || QFIds[type]?.includes(itemId));
              defaultItemIds = addItemsIds(data, defaultItemIds, type, ids);
            });
          }
        });
      }
    }

    return !isEqual(allItemsIds, defaultItemIds);
  }, [data, allItemsIds, activeQFLink]);

  const isDataChanged = useMemo(() => !isEqual(getAppliedItemsIds(data), allItemsIds), [data, allItemsIds]);

  return {
    allItemsIds,
    ariaLiveMessageItems,
    ariaLiveMessageRegions,
    destinationType,
    getRegions,
    isDataApplied,
    isDataChanged,
    isWholeChecked,
    onDestinationTypeChange,
    onItemCheck,
    onRegionCheck,
    onRegionSelect,
    onReset,
    onSubmit,
    popoverProps,
    resetToAppliedData,
    selectedRegion,
    selectedRegionId,
    warning,
  };
};

export default useDestinationRefinementData;
